import {
  Box,
  Button,
  Divider,
  Menu,
  MenuButton,
  MenuGroup,
  MenuList,
  Skeleton,
} from "@chakra-ui/react";
import React from "react";
import { RiMore2Fill } from "react-icons/ri";
import { GenericError } from "src/components/Feedback/GenericError";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { useFormTemplateContext } from "src/components/Providers/FormProvider";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import * as List from "src/services/list";
import * as GQL from "src/types/graphql";
import * as RD from "src/types/remoteData";
import { FETCH_OFFER_WAITLIST_STATUS } from "../graphql/queries";
import { SchoolFormId } from "../types";
import { AcceptOffer } from "./AcceptOffer";
import { AddToWaitlists } from "./AddToWaitlists";
import { ClearOfferExpiration } from "./ClearOfferExpiration";
import { ClearSubstatus } from "./ClearSubstatus";
import { DeclineOffer } from "./DeclineOffer";
import { ExcludeForm } from "./ExcludeForm";
import { MakeOffer } from "./MakeOffer";
import { RemoveFromWaitlists } from "./RemoveFromWaitlists";
import { RevokeOffer } from "./RevokeOffer";

const MenuItemSkeleton: React.FC = () => (
  <Box paddingX={3} paddingY={1.5}>
    <Skeleton height={5} />
  </Box>
);

const MenuSkeleton: React.FC = () => (
  <>
    <MenuGroup title="Waitlist">
      <MenuItemSkeleton />
      <MenuItemSkeleton />
    </MenuGroup>
    <Divider />
    <MenuGroup title="Offer">
      <MenuItemSkeleton />
      <MenuItemSkeleton />
      <MenuItemSkeleton />
      <MenuItemSkeleton />
      <MenuGroup>
        <Divider />
        <MenuItemSkeleton />
      </MenuGroup>
    </MenuGroup>
    <MenuGroup>
      <MenuItemSkeleton />
      <MenuItemSkeleton />
    </MenuGroup>
  </>
);

type Props = {
  selectedFormSchoolsRD: RD.RemoteData<unknown, SchoolFormId[]>;
  onRefetch?: () => void;
  onOffersAccepted?: () => void;
};
export const SingleMoreMenu: React.FC<Props> = ({
  selectedFormSchoolsRD,
  onRefetch,
  onOffersAccepted,
}) => {
  const searchKeys: GQL.form_school_rank_bool_exp[] = React.useMemo(
    () =>
      List.filterMap(selectedFormSchoolsRD.withDefault([]), (schoolFormId) => {
        if (!schoolFormId.schoolId) return null;

        return {
          form_id: { _eq: schoolFormId.formId },
          school_id: { _eq: schoolFormId.schoolId },
        };
      }),
    [selectedFormSchoolsRD]
  );

  const { remoteData: statusData } = useRemoteDataQuery<
    GQL.FetchOfferWaitlistStatus,
    GQL.FetchOfferWaitlistStatusVariables
  >(FETCH_OFFER_WAITLIST_STATUS, {
    variables: { search_keys: searchKeys },
    skip: !selectedFormSchoolsRD.hasData(),
    fetchPolicy: "cache-and-network",
  });

  const selectedFormSchoolStatusesRD = React.useMemo(() => {
    return statusData.map((data) => data.form_school_rank);
  }, [statusData]);

  const { formTemplateSettings } = useFormTemplateContext();

  return (
    <Menu placement="bottom-end">
      <MenuButton
        as={Button}
        variant="banner"
        size="sm"
        leftIcon={<RiMore2Fill />}
        onClick={() => {}}
      >
        More
      </MenuButton>

      <MenuList>
        <RemoteDataView
          remoteData={RD.toTuple(
            selectedFormSchoolStatusesRD,
            selectedFormSchoolsRD
          )}
          loading={<MenuSkeleton />}
          notAsked={<MenuSkeleton />}
          error={() => <GenericError />}
          config={{ showDataWhileReloading: false }}
        >
          {([selectedFormSchoolStatuses, selectedFormSchools]) => (
            <>
              <MenuGroup title="Waitlist">
                <AddToWaitlists
                  selectedFormSchoolStatuses={selectedFormSchoolStatuses}
                  selectedFormSchools={selectedFormSchools}
                  onRefetch={onRefetch}
                  menuLabel="Add to waitlist"
                />
                <RemoveFromWaitlists
                  selectedFormSchoolStatuses={selectedFormSchoolStatuses}
                  selectedFormSchools={selectedFormSchools}
                  onRefetch={onRefetch}
                  menuLabel="Remove from waitlist"
                />
              </MenuGroup>

              <Divider />

              <MenuGroup title="Offer">
                <MakeOffer
                  selectedFormSchoolStatuses={selectedFormSchoolStatuses}
                  selectedFormSchools={selectedFormSchools}
                  onRefetch={onRefetch}
                  menuLabel="Make offer"
                />
                <AcceptOffer
                  selectedFormSchoolStatuses={selectedFormSchoolStatuses}
                  selectedFormSchools={selectedFormSchools}
                  onRefetch={onRefetch}
                  menuLabel="Accept offer"
                  onOffersAccepted={onOffersAccepted}
                />
                <DeclineOffer
                  formTemplateSettings={formTemplateSettings}
                  selectedFormSchoolStatuses={selectedFormSchoolStatuses}
                  selectedFormSchools={selectedFormSchools}
                  onRefetch={onRefetch}
                  menuLabel="Decline offer"
                />
                <RevokeOffer
                  selectedFormSchoolStatuses={selectedFormSchoolStatuses}
                  selectedFormSchools={selectedFormSchools}
                  onRefetch={onRefetch}
                  menuLabel="Revoke offer"
                />
                <MenuGroup>
                  <Divider />
                  <ClearOfferExpiration
                    selectedFormSchoolStatuses={selectedFormSchoolStatuses}
                    selectedFormSchools={selectedFormSchools}
                    onRefetch={onRefetch}
                  />
                </MenuGroup>
              </MenuGroup>
              <MenuGroup>
                <ExcludeForm
                  selectedFormSchoolStatuses={selectedFormSchoolStatuses}
                  onRefetch={onRefetch}
                />
                <ClearSubstatus
                  selectedFormSchoolStatuses={selectedFormSchoolStatuses}
                  onRefetch={onRefetch}
                />
              </MenuGroup>
            </>
          )}
        </RemoteDataView>
      </MenuList>
    </Menu>
  );
};
